'use client';

import Link from 'next/link';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import CmsImage from '@/components/ui/CmsImage';
import { cn } from '@/components/ui/utils';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';

export type ProductRangeCarouselItem = {
  id: string;
  imageSrc: string;
  title: string;
  description: string;
  href?: string;
};

type ProductRangeCarouselProps = {
  items: ProductRangeCarouselItem[];
  className?: string;
};

/**
 * Horizontal carousel (Shadcn / Embla). Mobile: one slide; md+: two; lg+: three visible.
 */
export function ProductRangeCarousel({ items, className }: ProductRangeCarouselProps) {
  const loop = items.length > 3;

  return (
    <Carousel
      opts={{
        align: 'start',
        loop,
        containScroll: 'trimSnaps',
        // Allow Link clicks; use prev/next buttons for sliding (Embla drag blocks navigation).
        watchDrag: false,
      }}
      className={cn('w-full min-w-0', className)}
    >
      <div className="container relative mx-auto w-full min-w-0 px-4 sm:px-6 lg:px-0">
        <CarouselContent className="-ml-5 py-6 lg:-ml-6">
          {items.map((item) => {
            const card = (
              <article
                data-range-card
                className="group relative h-full overflow-hidden rounded-[1.35rem] border border-[#0a0a0a]/10 bg-white transition duration-500 hover:-translate-y-1"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
                  <div data-range-image className="absolute inset-0 h-[112%]">
                    <CmsImage
                      src={item.imageSrc}
                      alt={item.title}
                      fill
                      className="object-cover object-center transition duration-700 group-hover:scale-[1.06]"
                      sizes="(max-width: 768px) 100vw, 33vw"
                      priority={false}
                      fallbackSrc={MEDIA_PLACEHOLDER}
                    />
                  </div>
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,10,10,0)_48%,rgba(10,10,10,0.54)_100%)] opacity-70 transition-opacity duration-500 group-hover:opacity-90"
                  />
                </div>

                <div className="relative p-5 sm:p-6">
                  <div
                    aria-hidden
                    className="absolute left-6 top-0 h-[3px] w-12 -translate-y-1/2 rounded-full bg-red-600 transition-all duration-500 group-hover:w-20"
                  />
                  <h3 className="text-xl font-semibold leading-tight tracking-tight text-[#0a0a0a]">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#0a0a0a]/62 md:text-base">
                    {item.description}
                  </p>
                  <div className="mt-5 inline-flex items-center text-sm font-semibold text-red-600">
                    Learn more
                    <span
                      className="ml-2 transition-transform duration-300 group-hover:translate-x-1"
                      aria-hidden
                    >
                      →
                    </span>
                  </div>
                </div>
              </article>
            );

            return (
              <CarouselItem
                key={item.id}
                className={cn(
                  'min-w-0 shrink-0 grow-0 pl-5 lg:pl-6',
                  'basis-full',
                  'md:basis-1/2',
                  'lg:basis-1/3',
                )}
              >
                {item.href ? (
                  <Link href={item.href} className="block h-full">
                    {card}
                  </Link>
                ) : (
                  <div className="h-full">{card}</div>
                )}
              </CarouselItem>
            );
          })}
        </CarouselContent>

        <CarouselPrevious
          type="button"
          variant="outline"
          className={cn(
            'top-1/2 z-10 -translate-y-1/2 border-[#0A0A0A]/10 bg-white/90',
            'left-2 md:left-6',
            'disabled:invisible',
          )}
        />
        <CarouselNext
          type="button"
          variant="outline"
          className={cn(
            'top-1/2 z-10 -translate-y-1/2 border-[#0A0A0A]/10 bg-white/90',
            'right-2 md:right-6',
            'disabled:invisible',
          )}
        />
      </div>
    </Carousel>
  );
}
